import { FC, SVGProps } from 'react'
import { ReactComponent as MentionIcon } from 'src/assets/svg/akar-icons_mention.svg'
import { Skeleton } from 'src/components/Skeleton'
import styles from './totalMentionsCard.module.scss'

// One skeleton row per sentiment (positive / neutral / negative) so the loading
// card occupies the same three-row body as the real one.
const SENTIMENT_ROWS = 3

interface TotalMentionsCardProps {
  hasData?: boolean
  icon?: FC<SVGProps<SVGSVGElement>>
  isLoading?: boolean
  negativeCount?: number
  negativePercentage?: number
  neutralCount?: number
  neutralPercentage?: number
  positiveCount?: number
  positivePercentage?: number
  title?: string
  total?: number
}

export const TotalMentionsCard: FC<TotalMentionsCardProps> = ({
  hasData = false,
  icon: Icon = MentionIcon,
  isLoading = false,
  negativeCount = 0,
  negativePercentage = 0,
  neutralCount = 0,
  neutralPercentage = 0,
  positiveCount = 0,
  positivePercentage = 0,
  title = 'Total Mentions',
  total = 0,
}) => {
  const segments = [
    {
      color: '#8080FF',
      count: positiveCount.toLocaleString(),
      name: 'Positive',
      percentage: positivePercentage,
    },
    {
      color: '#D3D4D5',
      count: neutralCount.toLocaleString(),
      name: 'Neutral',
      percentage: neutralPercentage,
    },
    {
      color: '#FF6833',
      count: negativeCount.toLocaleString(),
      name: 'Negative',
      percentage: negativePercentage,
    },
  ]

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <Icon className={styles.icon} />
        <h3 className={styles.title}>{title}</h3>
        {/* .totalCount is 24px (totalMentionsCard.module.scss). Without this the
            header showed a confident "0" for every first load. */}
        {isLoading ? (
          <span className={styles.totalCount}>
            <Skeleton height={24} width={72} />
          </span>
        ) : (
          hasData && (
            <span className={styles.totalCount}>{total.toLocaleString()}</span>
          )
        )}
      </div>
      {isLoading ? (
        <div className={styles.content}>
          {Array.from({ length: SENTIMENT_ROWS }).map((_, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <div className={styles.sourceRow} key={index}>
              {/* .barWrapper is 10px tall; .stats is a fixed 130px column. */}
              <Skeleton height={10} radius={4} />
              <Skeleton height={14} width={130} />
            </div>
          ))}
        </div>
      ) : hasData ? (
        <div className={styles.content}>
          {segments.map((segment) => {
            return (
              <div className={styles.sourceRow} key={segment.name}>
                <div className={styles.barWrapper}>
                  <div
                    className={styles.bar}
                    style={{
                      backgroundColor: segment.color,
                      width: `${segment.percentage}%`,
                    }}
                  />
                </div>
                <div className={styles.stats}>
                  <div className={styles.labelGroup}>
                    <span className={styles.percentage}>
                      {segment.percentage}%
                    </span>
                    <span className={styles.sourceName}>{segment.name}</span>
                  </div>
                  <span className={styles.count}>{segment.count}</span>
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        <div className={styles.noData}>No Data</div>
      )}
    </div>
  )
}
